/** @jsx jsx */
import { forwardRef, ReactElement } from 'react';
import { jsx } from '@emotion/core';
import {
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import CloseIcon from '@material-ui/icons/Close';

import { Text, Title } from 'components';
import {
  CustomButtonType,
  MainButton,
  PrimaryButton,
  TertiaryButton,
} from 'components/Button';
import { ICustomTheme } from 'interfaces';
import {
  CloseButton,
  cssButton,
  cssDialogActions,
  cssDialogContentText,
  cssDialogTitle,
  cssTitle,
} from './styles';

const Transition = forwardRef<
  unknown,
  TransitionProps & {
    children?: ReactElement;
  }
>((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const DeclarationDialog = ({
  onCancel,
  onConfirm,
  adornment,
  title,
  explanation,
  cancelLabel,
  confirmLabel,
  isClosable,
  isOpen,
  confirmButtonProps,
  cancelButtonProps,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  adornment: JSX.Element;
  title: string;
  explanation: string;
  cancelLabel: string;
  confirmLabel: string;
  isClosable: boolean;
  isOpen: boolean;
  confirmButtonProps: CustomButtonType;
  cancelButtonProps: CustomButtonType;
}): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );

  return (
    <Dialog
      fullWidth
      PaperProps={{
        style: {
          position: 'relative',
          maxWidth: 750,
          padding: theme.spacing(5, 6),
        },
      }}
      style={{
        zIndex: 13000,
      }}
      fullScreen={isDownSm}
      open={isOpen}
      TransitionComponent={Transition}
      onClose={isClosable ? onCancel : undefined}
    >
      {!isDownSm && isClosable && (
        <CloseButton onClick={onCancel}>
          <CloseIcon fontSize="large" />
        </CloseButton>
      )}
      {adornment}
      <DialogTitle css={cssDialogTitle}>
        <Title css={cssTitle} size={isDownSm ? 'h4' : 'h3'} align="center">
          {title}
        </Title>
      </DialogTitle>
      <DialogContent>
        <Text
          css={cssDialogContentText}
          size={isDownSm ? 'body2' : 'body1'}
          align="center"
        >
          {explanation}
        </Text>
      </DialogContent>
      <DialogActions css={cssDialogActions}>
        {cancelButtonProps.color === 'primary' ? (
          <MainButton
            css={cssButton}
            size={isDownSm ? 'sm' : 'md'}
            btnType="button"
            color="secondaryInverted"
            onClick={onCancel}
            {...cancelButtonProps}
          >
            {cancelLabel}
          </MainButton>
        ) : (
          <TertiaryButton
            css={cssButton}
            size={isDownSm ? 'sm' : 'md'}
            btnType="button"
            color="secondaryInverted"
            onClick={onCancel}
            {...cancelButtonProps}
          >
            {cancelLabel}
          </TertiaryButton>
        )}

        <PrimaryButton
          css={cssButton}
          size={isDownSm ? 'sm' : 'md'}
          btnType="button"
          onClick={onConfirm}
          autoFocus
          {...confirmButtonProps}
        >
          {confirmLabel}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

DeclarationDialog.defaultProps = {
  cancelButtonProps: {},
  confirmButtonProps: {},
};
